import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  coverImage,
  audioMensajes,
  scoreAccionesAudio,
  reglasAudio,
  globalStatsAudio,
  usoLabels,
  estadoModeracionLabels,
  duracionLabels,
  getMasEscuchados,
  getMasCompartidos,
  getMayorScore,
  getMasRecientes,
} from '@/mocks/audioMensajes';
import type { UsoAudio, DuracionAudio, EstadoModeracion } from '@/mocks/audioMensajes';

type Ordenamiento = 'recientes' | 'mayor-score' | 'mas-escuchados' | 'mas-compartidos' | 'destacados';

export default function AudioMensajesPage() {
  const [ordenamiento, setOrdenamiento] = useState<Ordenamiento>('recientes');
  const [usoFiltro, setUsoFiltro] = useState<UsoAudio | 'todos'>('todos');
  const [duracionFiltro, setDuracionFiltro] = useState<DuracionAudio | 'todos'>('todos');
  const [estadoFiltro, setEstadoFiltro] = useState<EstadoModeracion | 'todos'>('todos');
  const [vista, setVista] = useState<'audios' | 'reglas'>('audios');
  const [audioExpandido, setAudioExpandido] = useState<number | null>(null);
  const [audioActivo, setAudioActivo] = useState<number | null>(null);
  const [modoEscucha, setModoEscucha] = useState(false);

  const ordenados = (() => {
    let base = [...audioMensajes];
    if (usoFiltro !== 'todos') base = base.filter((a) => a.uso === usoFiltro);
    if (duracionFiltro !== 'todos') base = base.filter((a) => a.duracion === duracionFiltro);
    if (estadoFiltro !== 'todos') base = base.filter((a) => a.estado === estadoFiltro);

    switch (ordenamiento) {
      case 'recientes':
        return base.sort((a, b) => new Date(b.fechaGrabacion).getTime() - new Date(a.fechaGrabacion).getTime());
      case 'mayor-score':
        return base.sort((a, b) => b.score - a.score);
      case 'mas-escuchados':
        return base.sort((a, b) => b.escuchas - a.escuchas);
      case 'mas-compartidos':
        return base.sort((a, b) => b.compartidos - a.compartidos);
      case 'destacados':
        return base.filter((a) => a.estado === 'destacado');
      default:
        return base;
    }
  })();

  const usoPills: { uso: UsoAudio | 'todos'; label: string; icono: string }[] = [
    { uso: 'todos', label: 'Todos', icono: 'ri-filter-line' },
    { uso: 'saludo', label: 'Saludos', icono: 'ri-message-3-line' },
    { uso: 'concurso', label: 'Concursos', icono: 'ri-trophy-line' },
    { uso: 'participacion', label: 'Participación', icono: 'ri-chat-smile-2-line' },
    { uso: 'mensaje', label: 'Mensajes', icono: 'ri-mail-line' },
  ];

  const duracionPills: { duracion: DuracionAudio | 'todos'; label: string }[] = [
    { duracion: 'todos', label: 'Todas' },
    { duracion: '30', label: '30s (Básico)' },
    { duracion: '60', label: '60s (Pro)' },
    { duracion: '90', label: '90s (Premium)' },
  ];

  const estadoPills: { estado: EstadoModeracion | 'todos'; label: string; dot: string }[] = [
    { estado: 'todos', label: 'Todos', dot: 'bg-foreground-400' },
    { estado: 'pendiente', label: 'Pendientes', dot: 'bg-foreground-400' },
    { estado: 'aprobado', label: 'Aprobados', dot: 'bg-emerald-500' },
    { estado: 'destacado', label: 'Destacados', dot: 'bg-amber-500' },
    { estado: 'rechazado', label: 'Rechazados', dot: 'bg-rose-500' },
  ];

  const ordenamientos: { id: Ordenamiento; label: string; icono: string }[] = [
    { id: 'recientes', label: 'Más Recientes', icono: 'ri-time-line' },
    { id: 'mayor-score', label: 'Mayor Score', icono: 'ri-flashlight-line' },
    { id: 'mas-escuchados', label: 'Más Escuchados', icono: 'ri-headphone-line' },
    { id: 'mas-compartidos', label: 'Más Compartidos', icono: 'ri-share-forward-line' },
    { id: 'destacados', label: 'Destacados', icono: 'ri-star-line' },
  ];

  const handleToggleEscucha = (id: number) => {
    if (audioActivo === id) {
      setAudioActivo(null);
      setModoEscucha(false);
    } else {
      setAudioActivo(id);
      setModoEscucha(true);
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      {/* Hero */}
      <div className="relative w-full h-[400px] md:h-[500px] overflow-hidden">
        <img
          src={coverImage}
          alt="Audio Mensajes"
          className="w-full h-full object-cover object-top"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-500/20 backdrop-blur-sm border border-violet-400/30 text-violet-200 text-[11px] font-bold mb-4">
            <span className="w-2 h-2 rounded-full bg-violet-400 animate-pulse"></span>
            AUDIO MENSAJES
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-3">
            Grabá, Participá y Hacete Escuchar
          </h1>
          <p className="text-white/70 text-sm md:text-base max-w-[560px] leading-relaxed">
            Audio mensajes de 30, 60 o 90 segundos según tu plan. Para saludos, concursos y participación. Con moderación completa. Nunca se reproducen automáticamente.
          </p>
          <div className="flex items-center gap-3 mt-6">
            <span className="text-white/80 text-[10px] bg-background-50/10 backdrop-blur-sm px-3 py-1.5 rounded-full">
              {globalStatsAudio.audiosHoy} audios hoy
            </span>
            <span className="text-white/80 text-[10px] bg-background-50/10 backdrop-blur-sm px-3 py-1.5 rounded-full">
              {globalStatsAudio.audiosAprobados} aprobados
            </span>
            <span className="text-white/80 text-[10px] bg-background-50/10 backdrop-blur-sm px-3 py-1.5 rounded-full">
              {globalStatsAudio.escuchasTotales.toLocaleString()} escuchas
            </span>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="sticky top-0 z-30 bg-background-50/95 backdrop-blur-md border-b border-background-200">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-3">
          <div className="flex items-center justify-between gap-4 overflow-x-auto">
            <div className="flex items-center gap-4 shrink-0">
              <Link to="/" className="flex items-center gap-1.5 text-foreground-400 hover:text-foreground-700 transition-colors">
                <i className="ri-arrow-left-line"></i>
                <span className="text-xs font-semibold">RFW</span>
              </Link>
              <div className="flex items-center gap-4">
                <span className="text-foreground-500 text-[10px] flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-violet-500"></span>
                  {globalStatsAudio.audiosHoy} hoy
                </span>
                <span className="text-foreground-500 text-[10px] flex items-center gap-1 hidden sm:inline">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                  {globalStatsAudio.audiosAprobados} aprobados
                </span>
                <span className="text-foreground-500 text-[10px] flex items-center gap-1 hidden md:inline">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                  {globalStatsAudio.audiosDestacados} destacados
                </span>
                <span className="text-foreground-400 text-[10px] hidden lg:inline">
                  Top: {globalStatsAudio.audioMasEscuchado}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => setVista('audios')}
                className={`px-3 py-1.5 rounded-full text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  vista === 'audios' ? 'bg-foreground-950 text-background-50' : 'bg-background-100 text-foreground-500'
                }`}
              >
                <i className="ri-mic-line mr-1"></i>Audios
              </button>
              <button
                onClick={() => setVista('reglas')}
                className={`px-3 py-1.5 rounded-full text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  vista === 'reglas' ? 'bg-foreground-950 text-background-50' : 'bg-background-100 text-foreground-500'
                }`}
              >
                <i className="ri-scales-line mr-1"></i>Reglas
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-6">
        {vista === 'reglas' ? (
          /* ---- REGLAS VIEW ---- */
          <div className="max-w-[1000px] mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-2">Reglas del Sistema de Audio Mensajes</h2>
              <p className="text-foreground-500 text-sm">Cómo funciona el sistema de audio mensajes en RFW. La regla fundamental: nunca reproducir automáticamente.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {reglasAudio.map((regla) => (
                <div key={regla.id} className="bg-background-50 rounded-2xl border border-background-200 p-6">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${regla.color} flex items-center justify-center shrink-0`}>
                      <i className={`${regla.icono} text-background-50 text-lg`}></i>
                    </div>
                    <div>
                      <h4 className="text-foreground-950 font-bold text-sm mb-2">{regla.titulo}</h4>
                      <p className="text-foreground-500 text-xs leading-relaxed">{regla.descripcion}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Score Section */}
            <div className="mb-8">
              <h3 className="text-foreground-950 font-bold text-sm mb-4 text-center">Score por Participar</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {scoreAccionesAudio.map((acc) => (
                  <div key={acc.id} className="bg-background-50 rounded-2xl border border-background-200 p-5 text-center">
                    <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${acc.color} flex items-center justify-center mx-auto mb-3`}>
                      <i className={`${acc.icono} text-background-50 text-lg`}></i>
                    </div>
                    <p className="text-foreground-950 font-bold text-sm mb-1">{acc.accion}</p>
                    <p className="text-foreground-500 text-xs leading-relaxed mb-3">{acc.descripcion}</p>
                    <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-100 text-emerald-700 text-xs font-bold">
                      <i className="ri-add-line"></i>+{acc.puntos} pts
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Stats Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              {[
                { label: 'Audios Hoy', valor: globalStatsAudio.audiosHoy },
                { label: 'Aprobados', valor: globalStatsAudio.audiosAprobados },
                { label: 'Destacados', valor: globalStatsAudio.audiosDestacados },
                { label: 'Score Generado', valor: globalStatsAudio.scoreTotalGenerado.toLocaleString() },
              ].map((s) => (
                <div key={s.label} className="bg-background-50 rounded-2xl border border-background-200 p-4 text-center">
                  <p className="text-foreground-950 font-bold text-xl">{s.valor}</p>
                  <p className="text-foreground-400 text-[10px]">{s.label}</p>
                </div>
              ))}
            </div>

            {/* breakdown */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: '30s (Básico)', valor: globalStatsAudio.plan30, icono: 'ri-timer-line', bg: 'bg-background-50' },
                { label: '60s (Pro)', valor: globalStatsAudio.plan60, icono: 'ri-timer-line', bg: 'bg-primary-50' },
                { label: '90s (Premium)', valor: globalStatsAudio.plan90, icono: 'ri-timer-line', bg: 'bg-amber-50' },
                { label: 'Escuchas Totales', valor: globalStatsAudio.escuchasTotales.toLocaleString(), icono: 'ri-headphone-line', bg: 'bg-violet-50' },
              ].map((item) => (
                <div key={item.label} className={`${item.bg} rounded-2xl border border-background-200 p-4 text-center`}>
                  <i className={`${item.icono} text-foreground-500 text-lg mb-1 block`}></i>
                  <p className="text-foreground-950 font-bold text-lg">{item.valor}</p>
                  <p className="text-foreground-400 text-[10px]">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        ) : (
          /* ---- AUDIOS VIEW ---- */
          <div>
            {/* Ordenamiento */}
            <div className="flex items-center gap-1.5 mb-4 overflow-x-auto">
              {ordenamientos.map((ord) => (
                <button
                  key={ord.id}
                  onClick={() => setOrdenamiento(ord.id)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    ordenamiento === ord.id
                      ? 'bg-foreground-950 text-background-50'
                      : 'bg-background-50 text-foreground-500 border border-background-200 hover:bg-background-200'
                  }`}
                >
                  <i className={ord.icono}></i>
                  {ord.label}
                </button>
              ))}
            </div>

            {/* Filtros */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
              {/* Uso */}
              <div className="flex items-center gap-1 flex-wrap">
                <span className="text-foreground-400 text-[10px] mr-1">Uso:</span>
                {usoPills.map((pill) => (
                  <button
                    key={pill.uso}
                    onClick={() => setUsoFiltro(pill.uso)}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                      usoFiltro === pill.uso
                        ? 'bg-foreground-950 text-background-50'
                        : 'bg-background-50 text-foreground-500 border border-background-200 hover:border-background-300'
                    }`}
                  >
                    <i className={`${pill.icono} text-[10px]`}></i>
                    {pill.label}
                  </button>
                ))}
              </div>

              {/* Duración */}
              <div className="flex items-center gap-1 flex-wrap">
                <span className="text-foreground-400 text-[10px] mr-1">Duración:</span>
                {duracionPills.map((pill) => (
                  <button
                    key={pill.duracion}
                    onClick={() => setDuracionFiltro(pill.duracion)}
                    className={`px-3 py-1.5 rounded-full text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                      duracionFiltro === pill.duracion
                        ? 'bg-foreground-950 text-background-50'
                        : 'bg-background-50 text-foreground-500 border border-background-200 hover:border-background-300'
                    }`}
                  >
                    {pill.label}
                  </button>
                ))}
              </div>

              {/* Estado */}
              <div className="flex items-center gap-1 flex-wrap">
                <span className="text-foreground-400 text-[10px] mr-1">Estado:</span>
                {estadoPills.map((pill) => (
                  <button
                    key={pill.estado}
                    onClick={() => setEstadoFiltro(pill.estado)}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                      estadoFiltro === pill.estado
                        ? 'bg-foreground-950 text-background-50'
                        : 'bg-background-50 text-foreground-500 border border-background-200 hover:border-background-300'
                    }`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${pill.dot}`}></span>
                    {pill.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Audios Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {ordenados.map((a) => {
                const usoInfo = usoLabels[a.uso];
                const estadoInfo = estadoModeracionLabels[a.estado];
                const durInfo = duracionLabels[a.duracion];
                const isExpanded = audioExpandido === a.id;
                const isPlaying = audioActivo === a.id;

                return (
                  <div
                    key={a.id}
                    onClick={() => setAudioExpandido(isExpanded ? null : a.id)}
                    className={`bg-background-50 rounded-2xl border p-5 hover:border-background-300 transition-colors cursor-pointer ${
                      a.estado === 'destacado' ? 'border-amber-200 bg-amber-50/20' :
                      a.estado === 'rechazado' ? 'border-rose-100 bg-rose-50/10' :
                      a.estado === 'aprobado' ? 'border-emerald-100 bg-emerald-50/10' :
                      'border-background-200'
                    }`}
                  >
                    {/* Header: Uso + Duración + Estado */}
                    <div className="flex items-center justify-between mb-3 flex-wrap gap-1.5">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md ${usoInfo.color} text-[9px] font-semibold`}>
                        <i className={`${usoInfo.icono} text-[9px]`}></i>
                        {usoInfo.label}
                      </span>
                      <div className="flex items-center gap-1.5">
                        <span className={`inline-flex items-center gap-0.5 ${durInfo.bg} ${durInfo.color} px-2 py-0.5 rounded-md text-[9px] font-semibold`}>
                          <i className="ri-timer-line text-[9px]"></i>
                          {durInfo.label}
                        </span>
                        <span className="flex items-center gap-1 text-[9px]">
                          <span className={`w-1.5 h-1.5 rounded-full ${estadoInfo.dot}`}></span>
                          <span className="text-foreground-500">{estadoInfo.label}</span>
                        </span>
                      </div>
                    </div>

                    {/* Audio Player */}
                    <div
                      className="flex items-center gap-3 mb-3 rounded-xl px-4 py-3 bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-100 cursor-pointer hover:border-violet-300 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleEscucha(a.id);
                      }}
                    >
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
                        isPlaying ? 'bg-gradient-to-br from-violet-600 to-purple-600' : 'bg-gradient-to-br from-violet-500 to-purple-500'
                      }`}>
                        <i className={`${isPlaying ? 'ri-pause-fill' : 'ri-play-fill'} text-background-50 text-base`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-end gap-0.5 mb-1">
                          {[...Array(7)].map((_, i) => (
                            <span
                              key={i}
                              className={`w-1 rounded-full transition-all ${isPlaying ? 'bg-violet-500 animate-pulse' : 'bg-violet-300'}`}
                              style={{
                                height: `${4 + Math.random() * 12}px`,
                                animationDelay: isPlaying ? `${i * 0.12}s` : '0s',
                              }}
                            ></span>
                          ))}
                        </div>
                        <div className="flex items-center justify-between">
                          <p className="text-violet-600 text-[10px] font-semibold">{a.duracionSegundos}s</p>
                          <p className="text-violet-400 text-[9px]">
                            {isPlaying ? 'Reproduciendo...' : 'Clic para escuchar'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Text */}
                    <p className="text-foreground-600 text-xs leading-relaxed mb-3">
                      &ldquo;{a.texto}&rdquo;
                    </p>

                    {/* Remitente + Plan */}
                    <div className="flex items-center gap-2 mb-3">
                      <img
                        src={a.remitenteAvatar}
                        alt={a.remitente}
                        className="w-7 h-7 rounded-full object-cover"
                        loading="lazy"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-foreground-600 text-[11px] font-medium">{a.remitente}</p>
                        <p className="text-foreground-400 text-[9px]">{a.remitenteCiudad}</p>
                      </div>
                      <span className="text-foreground-300 text-[9px] bg-background-100 px-2 py-0.5 rounded-md">{a.remitentePlan}</span>
                    </div>

                    {/* Destino */}
                    <div className="flex items-center gap-1 mb-3">
                      <i className="ri-broadcast-line text-foreground-400 text-[10px]"></i>
                      <span className="text-foreground-500 text-[10px]">{a.destinoNombre}</span>
                      <span className="text-foreground-300 text-[9px]">&bull; {a.destinoRadio}</span>
                    </div>

                    {/* Timeline */}
                    <div className="flex items-center gap-3 text-[9px] text-foreground-400 mb-3">
                      <span>Grabado: {new Date(a.fechaGrabacion).toLocaleDateString('es-CR', { day: 'numeric', month: 'short' })} {new Date(a.fechaGrabacion).toLocaleTimeString('es-CR', { hour: '2-digit', minute: '2-digit' })}</span>
                      {a.fechaDestacado && (
                        <>
                          <span>&bull;</span>
                          <span className="text-amber-600 flex items-center gap-1">
                            <i className="ri-star-fill text-[9px]"></i>
                            Destacado
                          </span>
                        </>
                      )}
                    </div>

                    {/* Rejected reason */}
                    {a.estado === 'rechazado' && a.motivoRechazo && (
                      <div className="mb-3 px-3 py-2 rounded-lg bg-rose-50 border border-rose-100 text-[9px] text-rose-600 leading-relaxed">
                        <i className="ri-close-circle-line mr-1"></i>
                        {a.motivoRechazo}
                      </div>
                    )}

                    {/* Expanded detail */}
                    {isExpanded && (
                      <div className="mt-3 pt-3 border-t border-background-100 space-y-2">
                        {a.fechaModeracion && (
                          <div className="flex items-center gap-2 text-[10px]">
                            <span className="text-foreground-400">Moderado:</span>
                            <span className="text-foreground-600">{new Date(a.fechaModeracion).toLocaleTimeString('es-CR', { hour: '2-digit', minute: '2-digit' })}</span>
                            <span className="text-foreground-400">&bull;</span>
                            <span className="text-foreground-500">{estadoInfo.label}</span>
                          </div>
                        )}
                        {a.estado === 'pendiente' && (
                          <div className="flex items-center gap-2 text-[10px] bg-background-100/80 rounded-lg px-3 py-2">
                            <i className="ri-time-line text-foreground-400"></i>
                            <span className="text-foreground-500">Tu audio está pendiente de moderación. El locutor lo escuchará pronto.</span>
                          </div>
                        )}
                        {a.estado === 'destacado' && (
                          <div className="flex items-center gap-2 text-[10px] bg-amber-50 rounded-lg px-3 py-2">
                            <i className="ri-star-fill text-amber-600"></i>
                            <span className="text-amber-700">¡Tu audio mensaje fue destacado! Salió al aire y toda la comunidad lo escuchó.</span>
                          </div>
                        )}
                        {a.estado === 'aprobado' && (
                          <div className="flex items-center gap-2 text-[10px] bg-emerald-50 rounded-lg px-3 py-2">
                            <i className="ri-check-line text-emerald-600"></i>
                            <span className="text-emerald-700">Audio aprobado. Está disponible para que la comunidad lo escuche.</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Stats Footer */}
                    <div className="flex items-center gap-4 pt-2 border-t border-background-100">
                      <span className="text-foreground-400 text-[10px] flex items-center gap-1">
                        <i className="ri-headphone-line"></i>{a.escuchas}
                      </span>
                      <span className="text-foreground-400 text-[10px] flex items-center gap-1">
                        <i className="ri-share-forward-line"></i>{a.compartidos}
                      </span>
                      <span className="text-emerald-700 text-[10px] font-semibold ml-auto flex items-center gap-0.5">
                        <i className="ri-flashlight-fill"></i>{a.score} pts
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {ordenados.length === 0 && (
              <div className="text-center py-16">
                <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-mic-line text-foreground-400 text-2xl"></i>
                </div>
                <p className="text-foreground-500 text-sm">No hay audio mensajes que mostrar con estos filtros.</p>
                <button
                  onClick={() => { setUsoFiltro('todos'); setDuracionFiltro('todos'); setEstadoFiltro('todos'); setOrdenamiento('recientes'); }}
                  className="mt-3 px-4 py-2 rounded-lg bg-background-100 text-foreground-600 text-xs font-semibold cursor-pointer hover:bg-background-200 transition-colors"
                >
                  Limpiar Filtros
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* CTA */}
      <div className="max-w-[1200px] mx-auto px-4 md:px-6 pb-12">
        <div className="bg-gradient-to-r from-violet-500 to-amber-500 rounded-3xl p-8 md:p-10 text-center">
          <h3 className="text-white font-bold text-lg md:text-xl mb-2">¿Querés Grabar un Audio Mensaje?</h3>
          <p className="text-white/80 text-sm max-w-[500px] mx-auto mb-6 leading-relaxed">
            Entrá a la Cabina de tu radio favorita y grabá tu audio mensaje. Elegí entre 30, 60 o 90 segundos según tu plan. Sumá score, participá en concursos y hacé que tu voz llegue al aire.
          </p>
          <Link
            to="/cabina-2"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-background-50 text-foreground-950 font-bold text-sm cursor-pointer hover:bg-background-100 transition-all whitespace-nowrap"
          >
            <i className="ri-broadcast-line"></i>
            Ir a la Cabina
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </div>
  );
}